import { Dialog } from "../../dialog";
import { Children, forwardRef, useMemo } from "react";
import { Content } from "../../placeholders";
import { Div } from "../../html";
import { augmentElement, getSlotKey, isNil, mergeProps, useSlots } from "../../shared";
import { StyleProvider } from "../../styling";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "section";

function useModalContentMarkup(content) {
  return useMemo(() => {
    const before = [];
    const cards = [];
    const after = [];
    let hasEncounteredCard = false;
    Children.forEach(content.props.children, (x, index) => {
      if (getSlotKey(x) === "card") {
        cards.push(augmentElement(x, {
          className: "o-ui-modal-choice",
          fluid: true,
          key: index
        }));
        hasEncounteredCard = true;
      } else {
        if (hasEncounteredCard) {
          after.push(x);
        } else {
          before.push(x);
        }
      }
    });
    const hasCards = cards.length > 0;

    if (hasCards && cards.length !== 2) {
      throw new Error("A choice modal must have exactly 2 card components.");
    }

    return {
      contentMarkup: /*#__PURE__*/_jsxs(Content, { ...content.props,
        children: [before, !hasCards ? undefined : /*#__PURE__*/_jsx(StyleProvider, {
          value: {
            button: {
              variant: "secondary"
            }
          },
          children: /*#__PURE__*/_jsx(Div, {
            className: "o-ui-modal-choice-container",
            children: cards
          })
        }), after]
      }),
      hasCards
    };
  }, [content]);
}

export function InnerModal({
  as = DefaultElement,
  children,
  dismissable = true,
  forwardedRef,
  fullscreen,
  zIndex = 1,
  ...rest
}) {
  const {
    button,
    "button-group": buttonGroup,
    content,
    footer,
    header,
    heading,
    illustration,
    image
  } = useSlots(children, useMemo(() => ({
    _: {
      required: ["heading", "content"]
    },
    button: null,
    "button-group": null,
    content: {
      className: "o-ui-modal-content"
    },
    footer: null,
    header: null,
    heading: null,
    illustration: null,
    image: null
  }), []));
  const {
    contentMarkup,
    hasCards
  } = useModalContentMarkup(content);
  const size = useMemo(() => {
    if (!isNil(image) || !isNil(illustration)) {
      return "md";
    }

    if (hasCards) {
      return "lg";
    }

    return fullscreen ? "fullscreen" : "sm";
  }, [fullscreen, image, illustration, hasCards]);
  return /*#__PURE__*/_jsxs(Dialog, { ...mergeProps(rest, {
      as,
      dismissable,
      ref: forwardedRef,
      size,
      zIndex
    }),
    children: [image, illustration, heading, header, contentMarkup, footer, button, buttonGroup]
  });
}
InnerModal.defaultElement = DefaultElement;
export const Modal = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerModal, { ...props,
  forwardedRef: ref
}));